/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// ── Chat Pane v2 open/closed state ─────────────────────────────────────────────
//
// Chat is one surface of the generalized right panel (see
// `@/rightPanel/rightPanelRoute`). The open state below is a chat-flavored view
// of that router: `chatPaneOpen$` is "the right panel is showing chat", and the
// open/close/toggle helpers set the panel's route to (or off) `'chat'`. Kept as
// this thin layer so the many chat call sites (BottomToolbar input, discuss
// ingress, deep links) don't each have to know about the router union.
//
// The panel is an OVERLAY, not a route: NARROW viewports show it full-screen over
// the page you were on; WIDE viewports dock it as a right rail beside
// `#main-content`. Either way, closing reveals the page underneath (no route
// change). Nothing here is persisted — opening is always an explicit user action.

import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import {
    closeRightPanel,
    openRightPanel,
    rightPanelRoute$,
    rightPanelShowing$,
    toggleRightPanel,
} from '@/rightPanel/rightPanelRoute'
import { globalData } from '@/util/globalData'

/** Reactive `true` while the right panel is showing the chat surface. */
export const chatPaneOpen$: ObservableValue<boolean> = rightPanelShowing$('chat')

/** Open the pane on the chat surface (input submit/focus, discuss tap, deep link). */
export function openChatPane(): void {
    openRightPanel('chat')
}

/** Close the pane when chat is the surface showing (leaves other surfaces alone). */
export function closeChatPane(): void {
    if (rightPanelRoute$.value === 'chat') closeRightPanel()
}

/** Toggle the chat surface on the right panel. */
export function toggleChatPane(): void {
    toggleRightPanel('chat')
}

// ── History view open/closed state ─────────────────────────────────────────────
//
// Whether the history list fills the pane over the transcript. Lives here (not
// inside ChatPane) so the header's history icon and the ChatHistoryView stay in
// sync off ONE source of truth: the header toggles it (and reflects an active
// state), the view reads it to show/hide. Closing the pane resets it so a reopen
// lands on the transcript, never a stale list — ChatPane owns that reset.
const historyOpen$ = globalData(() => data(false))

/** Reactive open state of the history view within the pane. */
export const chatHistoryOpen$: MutableValue<boolean> = historyOpen$

/** Return the pane to the transcript (leave the history view). */
export function closeChatHistory(): void {
    historyOpen$.value = false
}

// ── Quick-search view open/closed state ────────────────────────────────────────
//
// Whether the quick-search overlay fills the pane over the transcript. Peer of
// `historyOpen$`: the chat input bar's search button flips it, ChatSearchView
// reads it to show/hide. Owned here (not inside ChatPane) so the input-bar
// trigger and the view stay in sync off ONE source of truth. Closing the pane
// resets it (ChatPane owns that reset), and it is mutually exclusive with the
// history view — opening one closes the other (ChatPane wires that coupling).
const searchOpen$ = globalData(() => data(false))

/** Reactive open state of the quick-search overlay within the pane. */
export const chatSearchOpen$: MutableValue<boolean> = searchOpen$

// Whether the open quick-search overlay is COLLAPSED to a slim bar (minimized)
// instead of showing its bottom-sheet results. Peer of `searchOpen$`: the
// overlay's minimize toggle flips it, while opening — or re-opening via typing
// — always lands expanded and closing resets it, so a reopen never starts
// collapsed.
const searchMinimized$ = globalData(() => data(false))

/** Reactive collapsed-to-a-slim-bar state of the open quick-search overlay. */
export const chatSearchMinimized$: MutableValue<boolean> = searchMinimized$

/** Open the quick-search overlay (input-bar search button), always expanded. */
export function openChatSearch(): void {
    searchOpen$.value = true
    searchMinimized$.value = false
}

/** Return the pane to the transcript (leave the quick-search overlay). */
export function closeChatSearch(): void {
    searchOpen$.value = false
    searchMinimized$.value = false
}

// The typed query that drives the quick-search overlay's results. The chat
// input bar is the single input for the overlay (it has no box of its own), so
// the composer writes this and ChatSearchView reads it — one search, results in
// the overlay. Cleared when a result is picked so a stale term isn't left in the
// composer after navigating away.
const searchQuery$ = globalData(() => data(''))

/** Reactive query text feeding the quick-search overlay's result list. */
export const chatSearchQuery$: MutableValue<string> = searchQuery$

// Live count of quick-search results backing the overlay. ChatSearchView owns the
// SearchSelect controller and publishes its count here so the input bar's search
// glyph can gate its expand — a tap can't open an overlay with nothing to show.
const searchResultCount$ = globalData(() => data(0))

/** Reactive count of quick-search results (0 ⇒ nothing to expand into). */
export const chatSearchResultCount$: MutableValue<number> = searchResultCount$
